/**
 * Mic speaker labels are stored in a neutral "My mic — {letter}" form so that
 * the owner's display name can be resolved at render time from current user
 * state — this avoids baking a stale or empty name into transcripts when the
 * auth state isn't yet synced at entry creation time.
 *
 * Legacy entries persisted as "{Owner}'s mic — {letter}" (em-dash) or
 * "{Owner}'s mic - {letter}" (hyphen) are also recognised so old transcripts
 * re-resolve against the current owner when opened.
 */

const MIC_LABEL_RE = /^(?:.+'s mic|My mic) [—-] ([A-Z])$/

/** Neutral persisted label — no user lookup required at creation time. */
export function neutralMicLabel(bareDgIndex: string | number | undefined): string {
  const n = typeof bareDgIndex === 'number' ? bareDgIndex : parseInt(bareDgIndex ?? '0', 10)
  const letter = String.fromCharCode(65 + (isNaN(n) ? 0 : n))
  return `My mic - ${letter}`
}

/**
 * Resolve a mic speaker label against the current owner name.
 *
 * - Matches both "My mic — A" (new neutral form) and legacy "{Name}'s mic — A" shapes.
 * - When an owner name is available, rewrites the prefix to "{ownerName}'s mic — A".
 * - Non-mic speaker strings pass through unchanged.
 */
export function resolveMicLabel(speaker: string, ownerName: string | undefined): string {
  const match = speaker.match(MIC_LABEL_RE)
  if (!match) return speaker
  const letter = match[1]
  if (ownerName) return `${ownerName}'s mic - ${letter}`
  return speaker
}

/** True when the label matches either the new or legacy mic shape. */
export function isMicLabel(speaker: string): boolean {
  return MIC_LABEL_RE.test(speaker)
}

/** Mic speaker index from a `mic:N` key or a "My mic - E" label; null if neither. */
export function micIndexFromEntry(entry: { dgSpeakerIndex?: string; speaker?: string }): number | null {
  if (entry.dgSpeakerIndex?.startsWith('mic:')) {
    const n = parseInt(entry.dgSpeakerIndex.slice(4), 10)
    if (Number.isFinite(n)) return n
  }
  const match = entry.speaker?.match(MIC_LABEL_RE)
  if (match) return match[1].charCodeAt(0) - 65
  return null
}

/** One mic speaker across the whole list — render the owner's name, no letter.
 *  Electron counterpart of `buildHostMicContext` (`single`) in the gateway. */
export function isSingleMicSpeaker(
  entries: Array<{ isHost?: boolean; dgSpeakerIndex?: string; speaker?: string }>,
): boolean {
  const distinct = new Set<number>()
  for (const entry of entries) {
    if (!entry.isHost) continue
    const idx = micIndexFromEntry(entry)
    if (idx != null) distinct.add(idx)
  }
  return distinct.size === 1
}

/**
 * By mic epoch, the `mic:N` voice the on-device voice match found is the
 * owner's. Deepgram numbers mic voices from 0 on every fresh stream, so a
 * match holds only in the epoch it was made in. See `@shared/self-voice`.
 */
export type UserVoiceMatches = Record<number, string>

interface HostEntry {
  isHost?: boolean
  dgSpeakerIndex?: string
  speaker?: string
  micEpoch?: number
}

/** Whether the voice match found this mic entry's voice is the owner's. */
export function isMatchedUserVoice(entry: HostEntry, matches: UserVoiceMatches | undefined): boolean {
  if (!entry.isHost || !entry.dgSpeakerIndex || !matches) return false
  return matches[entry.micEpoch ?? 0] === entry.dgSpeakerIndex
}

/** Diariser placeholders, the only labels a mic label may paint over. */
const PLACEHOLDER_SPEAKER_RE = /^(?:speaker\s*\d+|you|other)$/i

export interface HostSpeakerContext {
  ownerName?: string
  /** Computed over the whole list, so it can't vary per entry. */
  singleMicSpeaker: boolean
  userVoices?: UserVoiceMatches
}

/**
 * The name a host-mic entry shows: an assigned name, else the owner's for a
 * lone mic voice or the voice matched to them, else `{owner}'s mic - X`.
 * Electron counterpart of `hostMicDisplayLabel` in the gateway.
 */
export function resolveHostSpeaker(entry: HostEntry & { speaker: string }, ctx: HostSpeakerContext): string {
  const { speaker } = entry
  const isPlaceholder = !speaker.trim() || PLACEHOLDER_SPEAKER_RE.test(speaker.trim())
  // Painting a mic label over an assigned name would undo a rename.
  if (!isMicLabel(speaker) && !isPlaceholder) return speaker
  if (ctx.ownerName && (ctx.singleMicSpeaker || isMatchedUserVoice(entry, ctx.userVoices))) return ctx.ownerName
  const base = isMicLabel(speaker) ? speaker : neutralMicLabel(entry.dgSpeakerIndex?.replace(/^mic:/, ''))
  return resolveMicLabel(base, ctx.ownerName)
}
